import { Order } from "@shared-logic/orders"; //TODO: Modificar, no deberíamos importar ordenes
import { COMMODITIES_CODE, COMMODITIES_CODE_SELLER, HUGHES_CODE_BUYER, HUGHES_CODE_SELLER, HUGHES_CRAMDOWN_BUYER, HUGHES_CRAMDOWN_SELLER, HUGHES_OTHER_BROKER_BUYER, HUGHES_OTHER_BROKER_SELLER, NUTRIEN_CUIT, SANCOR_CODE_SELLER, VICENTIN_ARGENTINA_BUYER, VICENTIN_ARGENTINA_SELLER, VICENTIN_CRAMDOWN_BUYER, VICENTIN_CRAMDOWN_SELLER, VICENTIN_FEED_BUYER, VICENTIN_FEED_SELLER } from "./constants/subjects.codes.consts";
import { BROKERS_CONST } from "./constants/brokers";
import { BUYER_COMMISION, SELLER_COMMISION } from "./constants/subject.commissions";
import { buyerIsExchanger } from "./business-validation.service";
import { PRODUCT_NAMES_CONST } from "@shared-logic/products";
import { CREATION_TYPE_CONST } from "@shared-logic/prices";

// Productos habilitados para la marca Cramdown. Array extensible: sumar un producto futuro
// es agregar una línea acá, sin tocar la lógica de isCramdownEligibleProduct.
export const CRAMDOWN_ELIGIBLE_PRODUCTS: number[] = [PRODUCT_NAMES_CONST.SOJA];

export function isCramdownEligibleProduct(productName: number) {
    return CRAMDOWN_ELIGIBLE_PRODUCTS.includes(productName);
}

//Retorna true si el código pasado por parametro pertenece al de sancor.
export function isSancor(sellerCode: number) {
    return sellerCode == SANCOR_CODE_SELLER;
}

//Retorna true si es el cuit de Nutrien
export function isNutrienCuit(cuit: string) {
    return cuit == NUTRIEN_CUIT;
}

export function isBuyerOfGrassiGroup(buyerCode: number) {
    return [COMMODITIES_CODE,
        HUGHES_CODE_BUYER,
        HUGHES_CRAMDOWN_BUYER,
        VICENTIN_CRAMDOWN_BUYER,
        VICENTIN_ARGENTINA_BUYER,
        VICENTIN_FEED_BUYER,
        HUGHES_OTHER_BROKER_BUYER,
    ].includes(buyerCode);
}

export function isSellerOfGrassiGroup(sellerCode: number) {
    return [COMMODITIES_CODE_SELLER,
        HUGHES_CODE_SELLER,
        HUGHES_CRAMDOWN_SELLER,
        VICENTIN_CRAMDOWN_SELLER,
        VICENTIN_ARGENTINA_SELLER,
        VICENTIN_FEED_SELLER,
        HUGHES_OTHER_BROKER_SELLER
    ].includes(sellerCode);
}

export function isCodeCommoditiesOrHughesBuyer(buyerCode: number) {
    return [COMMODITIES_CODE, HUGHES_CODE_BUYER].includes(buyerCode);
}

export function isSellerCommoditiesOrHughes(sellerCode: number) {
    return sellerCode == COMMODITIES_CODE_SELLER || sellerCode == HUGHES_CODE_SELLER;
}

// Retorna true si el comprador NO requiere un puerto específico (Commodities, Hughes, Vicentin Cramdown, etc.)
export function isBuyerWithNoSpecificPort(buyerCode: number) {
    return [
        COMMODITIES_CODE,
        HUGHES_CODE_BUYER,
        HUGHES_CRAMDOWN_BUYER,
        VICENTIN_CRAMDOWN_BUYER,
        HUGHES_OTHER_BROKER_BUYER
    ].includes(buyerCode);
}

export function isBuyerAllowsAnotherBroker(buyerCode: number) {
    return [
        VICENTIN_ARGENTINA_BUYER,
        HUGHES_CRAMDOWN_BUYER,
        HUGHES_OTHER_BROKER_BUYER
    ].includes(buyerCode);
}

export function isBuyerRequiresProposalType(buyerCode: number) {
    return buyerCode == VICENTIN_CRAMDOWN_BUYER;
}

export function isCramdownBuyer(buyerCode: number) {
    return buyerCode == HUGHES_CRAMDOWN_BUYER || isBuyerRequiresProposalType(buyerCode);
}

export function isCramdownSeller(sellerCode: number) {
    return sellerCode == HUGHES_CRAMDOWN_SELLER || sellerCode == VICENTIN_CRAMDOWN_SELLER;
}

export function isCramdownApplicable(
    sellerCode: number,
    buyerCode: number,
    productName: number
): boolean {
    return isCramdownEligibleProduct(productName) &&
        (isCramdownSeller(sellerCode) || isCramdownBuyer(buyerCode));
}

// Resuelve la Marca (creationType) que corresponde a una combinación vendedor/comprador/producto.
// Mismo criterio que ya aplican los normalizadores de orden y de condición; se centraliza acá para
// que el alta multi-comprador de condiciones pueda resolver la Marca por comprador sin duplicar
// el ternario contra isCramdownApplicable.
export function resolveCreationType(
    sellerCode: number,
    buyerCode: number,
    productName: number
): number {
    return isCramdownApplicable(sellerCode, buyerCode, productName)
        ? CREATION_TYPE_CONST.CRAMDOWN
        : CREATION_TYPE_CONST.STANDARD;
}

// Bloqueo duro: a diferencia de isCramdownApplicable (que solo decide si corresponde la Marca),
// esta función determina si la combinación debe impedir el avance del operador. Acepta un array
// de candidatos de comprador (no un único código) porque "Solo le vende a" (order.buyer.onlySell)
// puede restringir a varios compradores antes de que exista un comprador confirmado en
// price.observations.buyer, que es lo que isCramdownApplicable lee. Reutiliza los mismos
// predicados de cuenta y de producto para no divergir del set de cuentas Cramdown aprobadas.
export function isCramdownProductMismatch(
    sellerCode: number,
    buyerCandidates: number[],
    productName: number
): boolean {
    return (isCramdownSeller(sellerCode) || buyerCandidates.some(isCramdownBuyer)) &&
        !isCramdownEligibleProduct(productName);
}

// Other broker (broke = 8): only the side that is NOT from the group is charged commission.
// Always the default commission, never the table value (business decision 2026-10-06).
// Any other seller/buyer combination (e.g. both from the group) keeps the current result.
function applyOtherBrokerCommission(order: Order, sellerCode: number, buyerCode: number): void {
    if (order.broke !== BROKERS_CONST.BROKER_OTRO) {
        return;
    }

    const sellerInGroup = isSellerOfGrassiGroup(sellerCode);
    const buyerInGroup = isBuyerOfGrassiGroup(buyerCode);

    if (!sellerInGroup && buyerInGroup) {
        order.commision.seller = -1;
        order.commision.buyer = BUYER_COMMISION;
    } else if (sellerInGroup && !buyerInGroup) {
        order.commision.seller = SELLER_COMMISION;
        order.commision.buyer = -1;
    }
}

// Negocio AV: both commissions are 0. Must run last so it prevails over every other rule.
// A missing avBusiness (orders created before the mark existed) is treated as false.
function applyAvBusinessCommission(order: Order): void {
    if (order.price.businessParticularities?.avBusiness === true) {
        order.commision.seller = 0;
        order.commision.buyer = 0;
    }
}

// Helper para setear comisiones y puerto de la orden
export function applyOrderCommissionAndPort(
    order: Order,
    sellerCode: number,
    buyerCode: number,
    getCommisionByOrder: (order: Order, type: 'C' | 'V') => { seller: number; buyer: number }
): Order {
    let orderCopy: Order = JSON.parse(JSON.stringify(order));

    if (buyerCode === VICENTIN_CRAMDOWN_BUYER) {
        orderCopy.commision.buyer = 0;
        orderCopy.commision.seller = 0;
        orderCopy.price.observations.isPort = -1; // Grassi Group behavior
        return orderCopy;
    }

    if (isSellerOfGrassiGroup(sellerCode)) {
        orderCopy.commision.seller = -1;

        if (sellerCode === HUGHES_OTHER_BROKER_SELLER && order.broke === BROKERS_CONST.BROKER_OTRO) {
            orderCopy.commision.seller = 0.5;
            orderCopy.commision.buyer = 1;
        }
    }

    if (isBuyerOfGrassiGroup(buyerCode)) {
        orderCopy.commision.buyer = -1;

        if (buyerCode === HUGHES_CRAMDOWN_BUYER) {
            const commisionBuyer = getCommisionByOrder(orderCopy, 'C');
            orderCopy.commision.buyer = commisionBuyer.buyer > 0 ? commisionBuyer.buyer : 1;
        }

        if (buyerCode === HUGHES_OTHER_BROKER_BUYER && order.broke === BROKERS_CONST.BROKER_OTRO) {
            orderCopy.commision.buyer = 0.5;
            orderCopy.commision.buyer = 1;
        }

        orderCopy.price.observations.isPort = -1;

        const commisionSeller = getCommisionByOrder(orderCopy, 'V');
        orderCopy.commision.seller = commisionSeller.seller <= 0 ? -1 : commisionSeller.seller;

        if (isSellerOfGrassiGroup(sellerCode)) {
            orderCopy.commision.seller = -1;
        }
    } else {
        if (buyerIsExchanger(buyerCode)) {
            orderCopy.commision.buyer = -1;
        } else {
            const commisionBuyer = getCommisionByOrder(orderCopy, 'C');
            const commisionSeller = getCommisionByOrder(orderCopy, 'V');
            orderCopy.commision.buyer = commisionBuyer.buyer;
            orderCopy.commision.seller = commisionSeller.seller;
        }
        orderCopy.price.observations.isPort = 1;
    }

    applyOtherBrokerCommission(orderCopy, sellerCode, buyerCode);
    applyAvBusinessCommission(orderCopy);

    return orderCopy;
}
